import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../api/client";
import UploadImage from "../common/UploadImage";

const asList = (payload, ...keys) => {
  if (Array.isArray(payload)) return payload;
  for (const k of keys) {
    if (Array.isArray(payload?.[k])) return payload[k];
  }
  return Array.isArray(payload?.data) ? payload.data : [];
};

const money = (n) =>
  `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

export default function Overview() {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setStatus("loading");
    try {
      // Each panel is independent, so one failing endpoint should degrade that
      // panel rather than blank the whole dashboard.
      const [users, programs, events, team, donations] = await Promise.all([
        api.get("/users/users").catch(() => null),
        api.get("/program/getAllprograms").catch(() => null),
        api.get("/events/getallevents").catch(() => null),
        api.get("/teammembers/Allemployees").catch(() => null),
        api.get("/donate/getalldonations").catch(() => null),
      ]);

      setData({
        users: users ? asList(users.data, "users") : null,
        programs: programs ? asList(programs.data, "programs") : null,
        events: events ? asList(events.data, "events") : null,
        team: team ? asList(team.data, "employees") : null,
        donations: donations ? asList(donations.data, "donations") : null,
      });
      setStatus("ready");
    } catch (err) {
      setError(err.response?.data?.message || "Could not load dashboard data.");
      setStatus("error");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (status === "loading") {
    return (
      <div aria-busy="true">
        <div className="es-stats">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="es-stat">
              <div className="es-skeleton es-skeleton--text" style={{ width: "55%" }} />
              <div className="es-skeleton es-skeleton--text" style={{ width: "35%", height: "1.8rem" }} />
            </div>
          ))}
        </div>
        <span className="es-visually-hidden">Loading dashboard</span>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="es-alert es-alert--error" role="alert">
        {error}{" "}
        <button type="button" className="es-btn es-btn--ghost es-btn--sm" onClick={load}>
          Retry
        </button>
      </div>
    );
  }

  const { users, programs, events, team, donations } = data;

  const now = Date.now();
  const upcoming = (events || []).filter((e) => new Date(e.eventDate).getTime() >= now);
  const enrolled = (programs || []).reduce((sum, p) => sum + (p.participantsCount || 0), 0);
  const raised = (donations || []).reduce((sum, d) => sum + (d.amount || 0), 0);
  const verified = (users || []).filter((u) => u.verified).length;

  const recentDonations = [...(donations || [])]
    .sort((a, b) => new Date(b.eventDate || b.createdAt) - new Date(a.eventDate || a.createdAt))
    .slice(0, 5);

  const nextEvents = [...upcoming]
    .sort((a, b) => new Date(a.eventDate) - new Date(b.eventDate))
    .slice(0, 4);

  const topPrograms = [...(programs || [])]
    .sort((a, b) => (b.participantsCount || 0) - (a.participantsCount || 0))
    .slice(0, 5);

  const stats = [
    {
      label: "User accounts",
      value: users ? users.length : "—",
      note: users ? `${verified} verified` : "unavailable",
    },
    {
      label: "Active programs",
      value: programs ? programs.length : "—",
      note: programs ? `${enrolled} enrolments` : "unavailable",
    },
    {
      label: "Upcoming events",
      value: events ? upcoming.length : "—",
      note: events ? `${events.length} total` : "unavailable",
    },
    {
      label: "Donations received",
      value: donations ? money(raised) : "—",
      note: donations ? `${donations.length} gifts` : "unavailable",
    },
  ];

  return (
    <div className="es-rise">
      <div className="es-stats">
        {stats.map((s) => (
          <div key={s.label} className="es-stat">
            <p className="es-stat__label">{s.label}</p>
            <p className="es-stat__value">{s.value}</p>
            <p className="es-stat__note">{s.note}</p>
          </div>
        ))}
      </div>

      <div style={{
        display: "grid",
        gap: "1.5rem",
        gridTemplateColumns: "repeat(auto-fit, minmax(20rem, 1fr))",
        alignItems: "start",
      }}>
        <section className="es-card" aria-labelledby="ov-programs">
          <div className="es-card__body">
            <div style={{ display: "flex", alignItems: "baseline", gap: "1rem" }}>
              <h2 className="es-h2" id="ov-programs" style={{ margin: 0, fontSize: "1.2rem" }}>
                Most enrolled programs
              </h2>
              <Link to="/AdminDash/programPage" className="es-linkbtn" style={{ marginLeft: "auto" }}>
                Manage
              </Link>
            </div>

            {topPrograms.length === 0 ? (
              <div className="es-state" style={{ marginTop: "1rem" }}>
                <p className="es-state__title">No programs yet</p>
                <p>Create one to see enrolment figures here.</p>
              </div>
            ) : (
              <div className="es-table-wrap" style={{ marginTop: "1rem" }}>
                <table className="es-table es-table--numeric">
                  <caption className="es-visually-hidden">Programs by enrolment</caption>
                  <thead>
                    <tr>
                      <th scope="col">Program</th>
                      <th scope="col">Price</th>
                      <th scope="col">Enrolled</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topPrograms.map((p) => (
                      <tr key={p._id}>
                        <td style={{ color: "var(--es-ink)", fontWeight: 500 }}>{p.name}</td>
                        <td>{money(p.price)}</td>
                        <td>{p.participantsCount || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>

        <section className="es-card" aria-labelledby="ov-donations">
          <div className="es-card__body">
            <h2 className="es-h2" id="ov-donations" style={{ margin: 0, fontSize: "1.2rem" }}>
              Recent donations
            </h2>

            {recentDonations.length === 0 ? (
              <div className="es-state" style={{ marginTop: "1rem" }}>
                <p className="es-state__title">No donations recorded</p>
                <p>Completed Stripe payments appear here automatically.</p>
              </div>
            ) : (
              <div className="es-table-wrap" style={{ marginTop: "1rem" }}>
                <table className="es-table es-table--numeric">
                  <caption className="es-visually-hidden">Most recent donations</caption>
                  <thead>
                    <tr>
                      <th scope="col">Donor</th>
                      <th scope="col">Cause</th>
                      <th scope="col">Amount</th>
                      <th scope="col">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentDonations.map((d) => (
                      <tr key={d._id}>
                        <td style={{ color: "var(--es-ink)", fontWeight: 500 }}>{d.username}</td>
                        <td>{d.programOrEventName}</td>
                        <td>{money(d.amount)}</td>
                        <td>{formatDate(d.eventDate || d.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>

        <section className="es-card" aria-labelledby="ov-events">
          <div className="es-card__body">
            <h2 className="es-h2" id="ov-events" style={{ margin: 0, fontSize: "1.2rem" }}>
              Next events
            </h2>

            {nextEvents.length === 0 ? (
              <div className="es-state" style={{ marginTop: "1rem" }}>
                <p className="es-state__title">Nothing scheduled</p>
                <p>Upcoming events will be listed here.</p>
              </div>
            ) : (
              <ul style={{ listStyle: "none", margin: "1rem 0 0", padding: 0, display: "grid", gap: "0.85rem" }}>
                {nextEvents.map((e) => (
                  <li key={e._id} style={{ display: "flex", gap: "0.85rem", alignItems: "center" }}>
                    <UploadImage
                      file={e.imageevents}
                      alt=""
                      style={{ width: "3.25rem", height: "3.25rem", borderRadius: "var(--es-r-md)", objectFit: "cover", flex: "none" }}
                    />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 600, color: "var(--es-ink)" }}>{e.nameevents}</div>
                      <div className="es-muted">{formatDate(e.eventDate)}</div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>

        <section className="es-card" aria-labelledby="ov-team">
          <div className="es-card__body">
            <h2 className="es-h2" id="ov-team" style={{ margin: 0, fontSize: "1.2rem" }}>
              Team
            </h2>
            <p className="es-muted" style={{ marginTop: "0.5rem" }}>
              {team ? `${team.length} people listed` : "unavailable"}
            </p>

            {team && team.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: "0.6rem", marginTop: "1rem" }}>
                {team.slice(0, 8).map((m) => (
                  <UploadImage
                    key={m._id}
                    file={m.image}
                    alt={m.name}
                    title={`${m.name} — ${m.position}`}
                    style={{ width: "3rem", height: "3rem", borderRadius: "50%", objectFit: "cover" }}
                  />
                ))}
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
